import { Injectable, inject } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Firestore, collection, getCountFromServer, query, where } from '@angular/fire/firestore';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { NGXLogger } from 'ngx-logger';
import firebase from 'firebase/compat/app';
import { Observable, combineLatest, from, map, of, switchMap, catchError, startWith } from 'rxjs';
import { ChurchAffiliationInvite } from '../models/church-affiliation-invite.model';
import { Group } from '../models/group.model';
import { HierarchyNode, HierarchyNodeStats, NodePermission, NodeRole } from '../models/hierarchy-node.model';
import { ManagementAssignment } from '../models/management-assignment.model';
import { MembershipStatus } from '../models/membership.status';
import { MembershipRequestOutcome, OrganizationMember } from '../models/organization-member.model';
import { Organization } from '../models/organization.model';

export function isChurchLevelName(name: string | null | undefined): boolean {
  return (name || '').trim().toLowerCase() === 'church';
}

// Roles saved before finance access was removed may still carry 'view_finances'; drop it so it is
// neither shown nor written back when the role is next saved
function withoutFinancePermissions(role: NodeRole): NodeRole {
  return {
    ...role,
    permissions: (role.permissions || []).filter((p) => (p as string) !== 'view_finances'),
  };
}

@Injectable({
  providedIn: 'root',
})
export class ManagementService {
  private afs: AngularFirestore = inject(AngularFirestore);
  private firestore: Firestore = inject(Firestore);
  private logger: NGXLogger = inject(NGXLogger);

  constructor() {}

  /**
   * Fetches management assignments for a registered user
   */
  getMyManagementAssignments(userId: string): Observable<ManagementAssignment[]> {
    if (!userId) return of([]);

    return this.afs
      .collection<ManagementAssignment>('management_assignments', (ref) =>
        ref.where('userId', '==', userId).where('status', '==', 'active')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map((assignments) =>
          assignments.map((a) => ({
            ...a,
            organizationName: a.organizationName || 'Global Fellowship Network',
          }))
        )
      );
  }

  /**
   * Fetches an organization document
   */
  getOrganization(orgId: string): Observable<Organization | null> {
    if (!orgId) return of(null);

    return this.afs
      .collection('organizations')
      .doc<Organization>(orgId)
      .valueChanges({ idField: 'id' })
      .pipe(map((org) => org || null));
  }

  /**
   * Fetches all hierarchy nodes under an organization
   */
  getHierarchyNodes(orgId: string): Observable<HierarchyNode[]> {
    if (!orgId) return of([]);

    return this.afs
      .collection<any>('hierarchy_nodes', (ref) =>
        ref.where('organizationId', '==', orgId).where('status', '==', 'active')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map((nodes) =>
          nodes
            .map((n) => ({
              id: n.id,
              organizationId: n.organizationId,
              name: n.name || '',
              levelId: n.levelId || '',
              levelName: n.levelName || '',
              depth: typeof n.depth === 'number' ? n.depth : (n.parentId ? 1 : 0),
              parentId: n.parentId || null,
              path: n.path || '',
              ancestors: Array.isArray(n.ancestors) ? n.ancestors : [],
              directChurchCount: n.directChurchCount || 0,
              totalSubtreeChurchCount: n.totalSubtreeChurchCount || 0,
              status: n.status || 'active',
              isChurch: !!n.isChurch,
              churchId: n.churchId || undefined,
              roles: Array.isArray(n.roles) ? n.roles.map(withoutFinancePermissions) : [],
              // Published daily by the server's org analytics job; asOf kept as millis (store-serializable)
              stats: n.stats
                ? {
                    activeMembers: Number(n.stats.activeMembers) || 0,
                    asOf: n.stats.asOf?.toMillis?.() ?? null,
                  }
                : undefined,
            } as HierarchyNode))
            .sort((a, b) => (a.depth ?? 0) - (b.depth ?? 0))
        )
      );
  }

  /**
   * Fetches affiliated churches for an organization
   */
  getAffiliatedChurches(orgId: string): Observable<Group[]> {
    if (!orgId) return of([]);

    return this.afs
      .collection<any>('groups', (ref) =>
        ref.where('organizationId', '==', orgId)
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map((churches) =>
          churches.map(
            (c) =>
              ({
                id: c.id,
                name: c.name || '',
                organizationId: c.organizationId,
                parentNodeId: c.parentNodeId,
                hierarchyPath: c.hierarchyPath || '',
                ancestors: c.ancestors || [],
                address: c.address
                  ? {
                      city: c.address.city || '',
                      country: c.address.country || '',
                      buildingName: c.address.buildingName || '',
                      buildingNo: c.address.buildingNo || '',
                    }
                  : undefined,
                imageUrl: c.imageUrl || '',
                groupType: c.groupType || 'church',
              } as Group)
          )
        )
      );
  }

  /**
   * Fetches leadership assignments anchored at a specific node
   */
  getNodeLeaders(orgId: string, nodeId: string): Observable<ManagementAssignment[]> {
    if (!orgId || !nodeId) return of([]);

    return this.afs
      .collection<ManagementAssignment>('management_assignments', (ref) =>
        ref
          .where('organizationId', '==', orgId)
          .where('nodeId', '==', nodeId)
          .where('status', '==', 'active')
      )
      .valueChanges({ idField: 'id' });
  }

  /**
   * A church's organization, the units above it and their leaders, for its Governance & Affiliation
   * page. Built on the server (churchAffiliation_oversight, for active members of the church) so the
   * client never needs read access to the organization's whole chart and role assignments.
   */
  getChurchOversight(churchId: string): Observable<{
    organization: Organization | null;
    lineage: HierarchyNode[];
    leaders: ManagementAssignment[];
  }> {
    const empty = { organization: null, lineage: [] as HierarchyNode[], leaders: [] as ManagementAssignment[] };
    if (!churchId) return of(empty);
    return from(
      this.callFunction<{
        organization: Organization | null;
        lineage: HierarchyNode[];
        leaders: ManagementAssignment[];
      }>('churchAffiliation_oversight', { churchId })
    ).pipe(
      map((result) => ({
        organization: result?.organization || null,
        lineage: result?.lineage || [],
        leaders: result?.leaders || [],
      }))
    );
  }

  /**
   * Creates a new container under a parent. Runs in orgChart_createNode, which checks manage_nodes on
   * the parent, the level and a unique name, and works out the depth, path and ancestors.
   */
  async createHierarchyNode(node: Partial<HierarchyNode>): Promise<string> {
    // Churches join only through an accepted invitation or an approved join request
    if (isChurchLevelName(node.levelName)) {
      throw new Error('Churches cannot be created in the org chart. Invite an existing church instead.');
    }
    const result = await this.callFunction<{ nodeId: string }>('orgChart_createNode', {
      parentId: node.parentId,
      name: node.name,
      levelId: node.levelId,
    });
    return result.nodeId;
  }

  /**
   * Renames a container; the name is the only container field clients can change.
   * orgChart_renameNode checks manage_nodes and that the name is unique in the organization.
   */
  async updateHierarchyNode(nodeId: string, updates: Partial<HierarchyNode>): Promise<void> {
    await this.callFunction('orgChart_renameNode', { nodeId, name: updates.name });
  }

  /**
   * Saves a node's roles. orgChart_saveNodeRoles checks manage_roles, keeps church-synced roles as they
   * are, only accepts active org members and mirrors role members into management_assignments.
   */
  async saveNodeRoles(orgId: string, nodeId: string, roles: NodeRole[]): Promise<void> {
    await this.callFunction('orgChart_saveNodeRoles', { orgId, nodeId, roles });
  }

  /**
   * Reconciles orphaned or mock seed assignments with node roles and member profiles.
   * orgChart_reconcileAssignments only runs for org admins and skips everyone else.
   */
  async reconcileLegacyAssignments(orgId: string): Promise<void> {
    if (!orgId) return;
    try {
      await this.callFunction('orgChart_reconcileAssignments', { orgId });
    } catch (err) {
      this.logger.warn('management.service.ts :: reconcileLegacyAssignments non-critical error:', err);
    }
  }

  /**
   * Archives (soft-deletes) an empty container; orgChart_archiveNode checks manage_nodes
   */
  async deleteHierarchyNode(nodeId: string): Promise<void> {
    await this.callFunction('orgChart_archiveNode', { nodeId });
  }

  /**
   * Removes a node leader assignment, and the member from that role on the node
   */
  async removeNodeLeader(assignmentId: string): Promise<void> {
    await this.callFunction('orgChart_removeAssignment', { assignmentId });
  }

  /**
   * Fetches all organizations associated with a user (as assignment, member, creator, or member of a linked church)
   */
  getMyOrganizations(userId: string): Observable<Organization[]> {
    if (!userId) return of([]);

    const assignments$ = this.afs
      .collection<ManagementAssignment>('management_assignments', (ref) =>
        ref.where('userId', '==', userId)
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map((list) => list.filter((a) => !a.status || a.status === 'active')),
        catchError((err) => {
          this.logger.warn('management.service.ts :: assignments error:', err);
          return of([]);
        }),
        startWith([])
      );

    const memberships$ = this.afs
      .collection<OrganizationMember>('organization_members', (ref) =>
        ref.where('userId', '==', userId)
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map((list) => list.filter((m) => !m.status || m.status === 'active')),
        catchError((err) => {
          this.logger.warn('management.service.ts :: org memberships error:', err);
          return of([]);
        }),
        startWith([])
      );

    const created$ = this.afs
      .collection<Organization>('organizations', (ref) =>
        ref.where('createdBy', '==', userId)
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        catchError((err) => {
          this.logger.warn('management.service.ts :: created orgs error:', err);
          return of([]);
        }),
        startWith([])
      );

    // Orgs the user can open: they hold a role, are a member, or created it. Members of an affiliated
    // church view its org chart from the church page instead (getChurchOrgChart).
    return combineLatest([assignments$, memberships$, created$]).pipe(
      switchMap(([assignments, memberships, createdOrgs]) => {
        const orgIdSet = new Set<string>();
        assignments.forEach((a) => a.organizationId && orgIdSet.add(a.organizationId));
        memberships.forEach((m) => m.organizationId && orgIdSet.add(m.organizationId));
        createdOrgs.forEach((o) => o.id && orgIdSet.add(o.id));

        const orgIds = Array.from(orgIdSet);
        if (orgIds.length === 0) return of([]);

        const orgObservables = orgIds.map((id) =>
          this.getOrganization(id).pipe(
            map((org) => org || ({ id, name: 'Management Organization', rootNodeId: '', levelDefinitions: [], status: 'active' } as any)),
            catchError(() => of(null))
          )
        );

        return combineLatest(orgObservables).pipe(
          map((orgs) => orgs.filter((o): o is Organization => !!o && !!o.id))
        );
      })
    );
  }

  /**
   * Updates organization settings, metadata, and hierarchy level definitions.
   * orgChart_updateOrganization checks org admin and renames node levels to match.
   */
  async updateOrganization(orgId: string, updates: Partial<Organization>): Promise<void> {
    await this.callFunction('orgChart_updateOrganization', { orgId, updates });
  }

  /**
   * Moves an affiliated church to another container. Runs in churchAffiliation_move, which checks
   * manage_churches at both containers and updates the church group, its church-level node,
   * assignments and container counters (clients may not write a church's org placement).
   */
  async moveChurchAffiliation(orgId: string, churchId: string, targetNodeId: string): Promise<void> {
    if (!churchId || !targetNodeId) return;
    await this.callFunction('churchAffiliation_move', { orgId, churchId, targetNodeId });
  }

  /**
   * Fetches all members of an organization
   */
  getOrganizationMembers(orgId: string): Observable<OrganizationMember[]> {
    if (!orgId) return of([]);

    return this.afs
      .collection<OrganizationMember>('organization_members', (ref) =>
        ref.where('organizationId', '==', orgId)
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map((members) =>
          members.map((m) => ({
            ...m,
            role: m.role || 'member',
            status: m.status || MembershipStatus.active,
          }))
        )
      );
  }

  /**
   * True when the user owns the organization or is an *active* member with the admin role.
   * Removed, rejected or pending memberships never grant admin rights.
   */
  isOrganizationAdmin(
    org: Organization | null | undefined,
    myMembership: OrganizationMember | null | undefined,
    userId: string | null | undefined
  ): boolean {
    if (!userId) return false;
    if (org?.createdBy === userId) return true;
    return (
      myMembership?.userId === userId &&
      myMembership.role === 'admin' &&
      myMembership.status === MembershipStatus.active
    );
  }

  /**
   * True when the user is the owner or holds an active membership (pre-requisite for node-role permissions)
   */
  isActiveOrganizationMember(
    org: Organization | null | undefined,
    myMembership: OrganizationMember | null | undefined,
    userId: string | null | undefined
  ): boolean {
    if (!userId) return false;
    if (org?.createdBy === userId) return true;
    return myMembership?.userId === userId && myMembership.status === MembershipStatus.active;
  }

  /**
   * Consolidated member totals per unit (hierarchy_node_stats, published daily by the server),
   * keyed by node id. Readable by org members only; viewers from affiliated churches don't get them.
   */
  getUnitStats(orgId: string): Observable<Record<string, HierarchyNodeStats>> {
    if (!orgId) return of({});
    return this.afs
      .collection<any>('hierarchy_node_stats', (ref) => ref.where('organizationId', '==', orgId))
      .valueChanges({ idField: 'id' })
      .pipe(
        map((docs) =>
          Object.fromEntries(
            docs.map((d) => [
              d.id,
              {
                activeMembers: Number(d.activeMembers) || 0,
                churchCount: Number(d.churchCount) || 0,
                asOf: d.asOf?.toMillis?.() ?? null,
              } as HierarchyNodeStats,
            ])
          )
        ),
        catchError((err) => {
          this.logger.error('management.service.ts :: Error loading unit stats:', { orgId, err });
          return of({});
        })
      );
  }

  /** Active member count via a count query (~1 read per 1,000 members), without loading them */
  async countActiveOrganizationMembers(orgId: string): Promise<number> {
    if (!orgId) return 0;
    this.logger.debug('management.service.ts :: Counting active org members:', { orgId });
    const snap = await getCountFromServer(
      query(
        collection(this.firestore, 'organization_members'),
        where('organizationId', '==', orgId),
        where('status', '==', MembershipStatus.active)
      )
    );
    const count = snap.data().count;
    this.logger.debug('management.service.ts :: Active org member count:', { orgId, count });
    return count;
  }

  /**
   * Streams the current user's own membership document for an organization (null if none)
   */
  getMyMembership(orgId: string, userId: string): Observable<OrganizationMember | null> {
    if (!orgId || !userId) return of(null);
    return this.afs
      .collection('organization_members')
      .doc<OrganizationMember>(`${orgId}_${userId}`)
      .valueChanges({ idField: 'id' })
      .pipe(
        map((m) => (m ? (m as OrganizationMember) : null)),
        catchError((err) => {
          this.logger.error('management.service.ts :: Error loading my org membership:', { orgId, userId, err });
          return of(null);
        })
      );
  }

  /**
   * Invites a new member to the organization (org admins only, checked in orgMembers_invite).
   * Never downgrades an existing membership: active members are rejected with an error,
   * and pending requests are approved instead of being reset to "invited".
   */
  async inviteMember(
    orgId: string,
    email: string,
    name: string,
    role: 'admin' | 'member'
  ): Promise<OrganizationMember> {
    const result = await this.callFunction<{ member: OrganizationMember }>('orgMembers_invite', {
      orgId,
      email,
      name,
      role,
    });
    return result.member;
  }

  /**
   * Submits the signed-in user's request to join an organization via the public join link.
   * - Active members and pending requests are left untouched.
   * - A pending invitation (by uid, or by their verified email) is accepted and becomes an active membership.
   * - Otherwise (new, rejected or removed) a fresh request is created.
   */
  async requestMembership(
    orgId: string,
    user: {
      uid: string;
      name?: string;
      email?: string;
      photoURL?: string;
      contact?: any;
      address?: any;
      profession?: string;
      gender?: string;
    }
  ): Promise<{ member: OrganizationMember; outcome: MembershipRequestOutcome }> {
    const { name, photoURL, contact, address, profession, gender } = user;
    return this.callFunction('orgMembers_request', {
      orgId,
      profile: { name, photoURL, contact, address, profession, gender },
    });
  }

  /**
   * Updates an organization member's status or role (e.g. approve, reject, remove).
   * orgMembers_updateStatus checks org admin; leaving active status also revokes every node role
   * the user held, so a removed member cannot keep permissions through the org chart.
   */
  async updateMemberStatus(memberDocId: string, status: MembershipStatus, role?: 'admin' | 'member'): Promise<void> {
    await this.callFunction('orgMembers_updateStatus', { memberDocId, status, role });
  }

  /**
   * Fetches all active management assignments across the entire organization
   */
  getOrganizationAssignments(orgId: string): Observable<ManagementAssignment[]> {
    if (!orgId) return of([]);

    return this.afs
      .collection<ManagementAssignment>('management_assignments', (ref) =>
        ref.where('organizationId', '==', orgId).where('status', '==', 'active')
      )
      .valueChanges({ idField: 'id' });
  }

  /**
   * Assigns an organization member to a specific node role (orgChart_assignRoleMember checks manage_roles)
   */
  async assignMemberToNodeRole(
    orgId: string,
    nodeId: string,
    roleId: string,
    member: { userId: string; userName: string; userEmail: string; userPhotoUrl?: string }
  ): Promise<void> {
    await this.callFunction('orgChart_assignRoleMember', { orgId, nodeId, roleId, userId: member.userId });
  }

  /**
   * Removes an organization member from a specific node role (orgChart_removeRoleMember checks manage_roles)
   */
  async removeMemberFromNodeRole(orgId: string, nodeId: string, roleId: string, userId: string): Promise<void> {
    await this.callFunction('orgChart_removeRoleMember', { orgId, nodeId, roleId, userId });
  }

  // --- Church Affiliation Invites ---

  /**
   * Finds church groups by name prefix for the "invite existing church" search.
   * Firestore prefix matching is case-sensitive, so the term is tried as typed and capitalized.
   */
  async searchChurches(term: string, max = 8): Promise<Group[]> {
    const trimmed = term.trim();
    if (trimmed.length < 2) return [];
    this.logger.debug('management.service.ts :: searchChurches:', { term: trimmed });
    const variants = [...new Set([trimmed, trimmed.charAt(0).toUpperCase() + trimmed.slice(1)])];

    const snaps = await Promise.all(
      variants.map((v) =>
        this.afs
          .collection<Group>('groups', (ref) =>
            ref
              .where('groupType', '==', 'church')
              .orderBy('name')
              .startAt(v)
              .endAt(v + '\uf8ff')
              .limit(max)
          )
          .get()
          .toPromise()
      )
    );

    const byId = new Map<string, Group>();
    snaps.forEach((snap) =>
      snap?.docs.forEach((d) => byId.set(d.id, { ...(d.data() as Group), id: d.id }))
    );
    this.logger.debug(`management.service.ts :: searchChurches found ${byId.size} churches for "${trimmed}"`);
    return [...byId.values()].slice(0, max);
  }

  /**
   * Pending invitations sent by an organization and join requests sent to it (org chart side panel)
   */
  getOrganizationChurchInvites(orgId: string): Observable<ChurchAffiliationInvite[]> {
    if (!orgId) return of([]);
    return this.afs
      .collection<ChurchAffiliationInvite>('church_affiliation_invites', (ref) =>
        ref.where('organizationId', '==', orgId).where('status', '==', 'pending')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        catchError((err) => {
          this.logger.error('management.service.ts :: Error loading organization church invites:', { orgId, err });
          return of([]);
        })
      );
  }

  /**
   * Pending invitations received by a church and join requests it sent (church affiliation page)
   */
  getChurchAffiliationInvites(churchId: string): Observable<ChurchAffiliationInvite[]> {
    if (!churchId) return of([]);
    return this.afs
      .collection<ChurchAffiliationInvite>('church_affiliation_invites', (ref) =>
        ref.where('churchId', '==', churchId).where('status', '==', 'pending')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map((invites) => invites.filter((i) => !i.expiresAt || i.expiresAt.toMillis() > Date.now())),
        catchError((err) => {
          this.logger.error('management.service.ts :: Error loading church affiliation invites:', { churchId, err });
          return of([]);
        })
      );
  }

  /**
   * Finds active organizations by name prefix, for a church asking to join one
   */
  async searchOrganizations(term: string, max = 8): Promise<Organization[]> {
    const trimmed = term.trim();
    if (trimmed.length < 2) return [];
    this.logger.debug('management.service.ts :: searchOrganizations:', { term: trimmed });
    const variants = [...new Set([trimmed, trimmed.charAt(0).toUpperCase() + trimmed.slice(1)])];

    const snaps = await Promise.all(
      variants.map((v) =>
        this.afs
          .collection<Organization>('organizations', (ref) =>
            ref.orderBy('name').startAt(v).endAt(v + '\uf8ff').limit(max * 2)
          )
          .get()
          .toPromise()
      )
    );

    const byId = new Map<string, Organization>();
    snaps.forEach((snap) =>
      snap?.docs.forEach((d) => {
        const org = { ...(d.data() as Organization), id: d.id };
        if (org.status === 'active') byId.set(d.id, org);
      })
    );
    this.logger.debug(`management.service.ts :: searchOrganizations found ${byId.size} organizations for "${trimmed}"`);
    return [...byId.values()].slice(0, max);
  }

  /** Church side: ask an organization to take the church in; the organization chooses the placement */
  async requestToJoinOrganization(orgId: string, churchId: string, message?: string): Promise<void> {
    await this.callFunction('churchAffiliation_requestToJoin', { orgId, churchId, message });
  }

  /** Org side: approve a church's join request at `targetNodeId`, or decline it */
  async reviewChurchJoinRequest(
    inviteId: string,
    approve: boolean,
    targetNodeId?: string,
    reason?: string
  ): Promise<void> {
    await this.callFunction('churchAffiliation_reviewRequest', { inviteId, approve, targetNodeId, reason });
  }

  async sendChurchInvite(
    orgId: string,
    churchId: string,
    targetNodeId: string,
    message?: string
  ): Promise<void> {
    await this.callFunction('churchAffiliation_sendInvite', { orgId, churchId, targetNodeId, message });
  }

  /** Withdraws a pending invitation (org side) or join request (church side) */
  async cancelChurchInvite(inviteId: string): Promise<void> {
    await this.callFunction('churchAffiliation_cancelInvite', { inviteId });
  }

  async respondToChurchInvite(inviteId: string, accept: boolean, reason?: string): Promise<void> {
    await this.callFunction('churchAffiliation_respond', { inviteId, accept, reason });
  }

  /**
   * Removes a church from an organization (org side) or leaves it (church side).
   * Church-level roles are revoked; memberships that only existed for the church are removed.
   */
  async detachChurch(
    orgId: string,
    churchId: string,
    reason?: string
  ): Promise<{ affectedUsers: number; removedMemberships: number }> {
    return this.callFunction('churchAffiliation_detach', { orgId, churchId, reason });
  }

  /**
   * Active org members whose name or email starts with `term` (Elasticsearch on the server;
   * Firestore in the emulator). Used by member pickers instead of loading every member.
   */
  async searchOrganizationMembers(orgId: string, term: string, limit = 10): Promise<OrganizationMember[]> {
    const searchTerm = term.trim();
    if (!orgId || searchTerm.length < 2) return [];
    const result = await this.callFunction<{ members: OrganizationMember[] }>('orgMembers_search', {
      orgId,
      searchTerm,
      limit,
    });
    return result?.members || [];
  }

  private async callFunction<T = any>(name: string, data: Record<string, any>): Promise<T> {
    this.logger.debug(`management.service.ts :: Calling ${name} Cloud Function with params:`, data);
    try {
      const callable = httpsCallable<Record<string, any>, T>(getFunctions(), name);
      const result = await callable(data);
      this.logger.debug(`management.service.ts :: ${name} returned:`, result.data);
      return result.data;
    } catch (err: any) {
      // Lead with code and message as plain text so the console shows them without expanding an Object
      this.logger.error(
        `management.service.ts :: Error calling ${name}: [${err?.code || 'unknown'}] ${err?.message || err}`,
        JSON.stringify({ details: err?.details ?? null, params: data })
      );
      // A function that is missing (not deployed) or crashed reaches the client as a bare
      // "internal" error; otherwise surface the HttpsError message the function threw
      if (err?.code === 'functions/internal' && (!err?.message || err.message === 'internal')) {
        throw new Error('The server could not complete this request. Please try again later.');
      }
      throw new Error(err?.message || 'Something went wrong. Please try again.');
    }
  }
}
